/**
 * EvmLitePanel — Earned Value Management (Lite) metrics panel
 * for the Budget subtab within Performance.
 *
 * Professional tier only — renders only when PreflightItems have
 * gantt_task_id linkages (i.e., cost data is connected to WBS tasks).
 *
 * Computes from existing data (no new API):
 *   BAC  = Σ estimated_total (all Gantt-linked items)
 *   PV   = Σ estimated_total × (elapsed % of each task's duration)
 *   EV   = Σ estimated_total × (task percentComplete / 100)
 *   AC   = Σ actual_total (all Gantt-linked items)
 *   CV   = EV − AC          (positive = under budget)
 *   SV   = EV − PV          (positive = ahead of schedule)
 *   CPI  = EV / AC          (>1 = under budget)
 *   SPI  = EV / PV          (>1 = ahead of schedule)
 *
 * Color semantics follow CLAUDE.md reserved palette:
 *   Green  = healthy (CPI/SPI ≥ 1.0, or positive variance)
 *   Yellow = warning (CPI/SPI 0.9–1.0, or small negative variance)
 *   Red    = critical (CPI/SPI < 0.9, or large negative variance)
 *   Blue   = complete (100% done)
 *   Grey   = N/A (insufficient data)
 *
 * Standard tier — this IS the EVM dependency component.
 */

import React, { useMemo } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { preflightStore, storeKey } from "../../Data/store";
import { usePreflightLoader } from "../../hooks/usePreflightLoader";
import type { PreflightItem } from "../../Data/PreFlight/preflightTypes";
import type { GanttTask } from "../../api/ganttTasks";
import { formatCurrencyCompact } from "../../Data/Budget/currencyLookup";
import styles from "./Navigation.module.css";

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface EvmLitePanelProps {
  projectGuid: string;
  budgetCurrency: string;
  /** Gantt tasks for the project — used for schedule data join */
  scheduleTasks: GanttTask[];
  debugMode?: boolean;
}

/* ------------------------------------------------------------------ */
/*  EVM-Lite Computation Engine                                        */
/* ------------------------------------------------------------------ */

export interface EvmLiteMetrics {
  /** Budget at Completion — total planned budget for linked items */
  bac: number;
  /** Planned Value — what should have been spent by now */
  pv: number;
  /** Earned Value — value of work actually completed */
  ev: number;
  /** Actual Cost — what was actually spent */
  ac: number;
  /** Cost Variance (EV − AC) — positive = under budget */
  cv: number;
  /** Schedule Variance (EV − PV) — positive = ahead of schedule */
  sv: number;
  /** Cost Performance Index (EV / AC) — >1 = under budget */
  cpi: number | null;
  /** Schedule Performance Index (EV / PV) — >1 = ahead of schedule */
  spi: number | null;
  /** Estimate at Completion (BAC / CPI) */
  eac: number | null;
  /** Variance at Completion (BAC − EAC) — positive = under budget */
  vac: number | null;
  /** Number of preflight items linked to Gantt tasks */
  linkedItemCount: number;
  /** Overall % complete (weighted by estimated_total) */
  weightedPctComplete: number;
}

/**
 * Calculate the elapsed percentage of a task's duration as of today.
 * Returns 0–100 clamped. If dates are missing/invalid, returns 0.
 */
function taskElapsedPct(startDate: string | null, endDate: string | null): number {
  if (!startDate || !endDate) return 0;
  const s = Date.parse(startDate);
  const e = Date.parse(endDate);
  const now = Date.now();
  if (isNaN(s) || isNaN(e) || e <= s) return 0;
  if (now <= s) return 0;
  if (now >= e) return 100;
  return ((now - s) / (e - s)) * 100;
}

/**
 * Compute EVM-Lite metrics by joining PreflightItems to GanttTasks.
 */
export function computeEvmLite(
  items: PreflightItem[],
  tasks: GanttTask[],
): EvmLiteMetrics {
  // Build a lookup of Gantt tasks by ID (case-insensitive)
  const taskMap = new Map<string, GanttTask>();
  for (const t of tasks) {
    if (t.id) taskMap.set(t.id.toUpperCase(), t);
  }

  let bac = 0;
  let pv = 0;
  let ev = 0;
  let ac = 0;
  let linkedCount = 0;
  let weightedPctNum = 0;  // numerator for weighted % complete
  let weightedPctDen = 0;  // denominator

  for (const item of items) {
    if (item.state === "withdrawn") continue;
    if (!item.gantt_task_id) continue;

    const task = taskMap.get(item.gantt_task_id.toUpperCase());
    if (!task) continue;

    const est = item.estimated_total ?? (
      item.unit_cost != null && item.target_quantity != null
        ? item.unit_cost * item.target_quantity
        : 0
    );
    const act = item.actual_total ?? (
      item.actual_cost != null
        ? item.actual_cost * item.current_quantity
        : 0
    );

    if (est <= 0 && act <= 0) continue;

    linkedCount++;
    bac += est;
    ac += act;

    // PV: what portion of this item's budget should have been spent
    // based on the elapsed calendar time of the linked task
    const elapsed = taskElapsedPct(task.startDate, task.endDate) / 100;
    pv += est * elapsed;

    // EV: what portion of this item's budget has been "earned"
    // based on the task's actual progress
    const pctComplete = Math.min(100, Math.max(0, task.percentComplete)) / 100;
    ev += est * pctComplete;

    // Weighted % complete
    weightedPctDen += est;
    weightedPctNum += est * pctComplete;
  }

  const cv = ev - ac;
  const sv = ev - pv;
  const cpi = ac > 0 ? ev / ac : null;
  const spi = pv > 0 ? ev / pv : null;
  const eac = cpi != null && cpi > 0 ? bac / cpi : null;
  const vac = eac != null ? bac - eac : null;
  const weightedPctComplete = weightedPctDen > 0
    ? Math.round((weightedPctNum / weightedPctDen) * 100)
    : 0;

  return {
    bac, pv, ev, ac, cv, sv, cpi, spi, eac, vac,
    linkedItemCount: linkedCount,
    weightedPctComplete,
  };
}

/* ------------------------------------------------------------------ */
/*  Status Color Helpers                                               */
/* ------------------------------------------------------------------ */

/**
 * Color for CPI/SPI index values using reserved palette.
 *   ≥ 1.0  → green (on track / under budget)
 *   0.9–1.0 → yellow (warning)
 *   < 0.9  → red (critical)
 */
function indexColor(val: number | null): string {
  if (val == null) return "var(--text-muted)";
  if (val >= 1.0) return "var(--color-success)";
  if (val >= 0.9) return "var(--color-warning)";
  return "var(--color-error)";
}

/**
 * Color for variance values (CV, SV).
 *   ≥ 0    → green (favorable)
 *   small negative (> -5% of BAC) → yellow (warning)
 *   large negative → red (critical)
 */
function varianceColor(val: number, bac: number): string {
  if (val >= 0) return "var(--color-success)";
  const pctOfBac = bac > 0 ? Math.abs(val) / bac : 0;
  if (pctOfBac < 0.05) return "var(--color-warning)";
  return "var(--color-error)";
}

/** Format index to 2 decimal places */
function fmtIndex(val: number | null): string {
  if (val == null) return "—";
  return val.toFixed(2);
}

/** Format variance with +/- sign */
function fmtVariance(val: number, currency: string): string {
  const prefix = val >= 0 ? "+" : "";
  return prefix + formatCurrencyCompact(val, currency);
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const EvmLitePanel: React.FC<EvmLitePanelProps> = ({
  projectGuid,
  budgetCurrency,
  scheduleTasks,
  debugMode,
}) => {
  const { t: tKpi } = useAppTranslation("kpi");

  /* ── Load preflight items ── */
  const { preflightVersion } = usePreflightLoader(projectGuid, true);

  const items: PreflightItem[] = useMemo(() => {
    void preflightVersion;
    if (!projectGuid) return [];
    return preflightStore.get(storeKey(projectGuid)) || [];
  }, [projectGuid, preflightVersion]);

  /* ── Compute EVM metrics ── */
  const metrics = useMemo(
    () => computeEvmLite(items, scheduleTasks),
    [items, scheduleTasks],
  );

  /* ── Gate: only render if there are Gantt-linked cost items ── */
  if (metrics.linkedItemCount === 0) return null;

  /* ── Render ── */
  return (
    <div className={styles.breakdownPanel}>
      {/* Section header */}
      <div className={styles.breakdownTitle}>
        {tKpi("budget.evm.title")}
      </div>

      {/* Subtitle showing data coverage */}
      <div style={{
        fontSize: 11,
        color: "var(--text-tertiary)",
        marginBlockEnd: 10,
      }}>
        {tKpi("budget.evm.coverage", {
          count: metrics.linkedItemCount,
        })}
      </div>

      {/* Primary metric row: CPI + CV (cost-focused only; schedule metrics live on Schedule tab) */}
      <div className={styles.evmMetricRow}>
        <div className={styles.evmMetricCard}>
          <span className={styles.evmMetricLabel}>
            {tKpi("budget.evm.cpi")}
          </span>
          <span
            className={styles.evmMetricValue}
            style={{ color: indexColor(metrics.cpi) }}
          >
            {fmtIndex(metrics.cpi)}
          </span>
          <span className={styles.evmMetricHint}>
            {tKpi("budget.evm.cpiHint")}
          </span>
        </div>
        <div className={styles.evmMetricCard}>
          <span className={styles.evmMetricLabel}>
            {tKpi("budget.evm.cv")}
          </span>
          <span
            className={styles.evmMetricValue}
            style={{ color: varianceColor(metrics.cv, metrics.bac) }}
          >
            {fmtVariance(metrics.cv, budgetCurrency)}
          </span>
        </div>
      </div>

      {/* Summary row: BAC, EV, AC, PV */}
      <table className={styles.breakdownTable}>
        <thead>
          <tr>
            <th className={styles.breakdownTh} style={{ textAlign: "start" }}>
              {tKpi("budget.evm.metric")}
            </th>
            <th className={styles.breakdownTh}>
              {tKpi("budget.evm.value")}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className={styles.breakdownTr}>
            <td className={styles.breakdownTdName}>
              {tKpi("budget.evm.bac")}
            </td>
            <td className={styles.breakdownTdNum}>
              {formatCurrencyCompact(metrics.bac, budgetCurrency)}
            </td>
          </tr>
          <tr className={styles.breakdownTr}>
            <td className={styles.breakdownTdName}>
              {tKpi("budget.evm.pvLabel")}
            </td>
            <td className={styles.breakdownTdNum}>
              {formatCurrencyCompact(metrics.pv, budgetCurrency)}
            </td>
          </tr>
          <tr className={styles.breakdownTr}>
            <td className={styles.breakdownTdName}>
              {tKpi("budget.evm.evLabel")}
            </td>
            <td className={styles.breakdownTdNum}>
              {formatCurrencyCompact(metrics.ev, budgetCurrency)}
            </td>
          </tr>
          <tr className={styles.breakdownTr}>
            <td className={styles.breakdownTdName}>
              {tKpi("budget.evm.acLabel")}
            </td>
            <td className={styles.breakdownTdNum}>
              {formatCurrencyCompact(metrics.ac, budgetCurrency)}
            </td>
          </tr>
          {metrics.eac != null && (
            <tr className={styles.breakdownTr}>
              <td className={styles.breakdownTdName}>
                {tKpi("budget.evm.eac")}
              </td>
              <td className={styles.breakdownTdNum}>
                {formatCurrencyCompact(metrics.eac, budgetCurrency)}
              </td>
            </tr>
          )}
          {metrics.vac != null && (
            <tr className={styles.breakdownTr}>
              <td className={styles.breakdownTdName}>
                {tKpi("budget.evm.vacLabel")}
              </td>
              <td className={styles.breakdownTdNum} style={{
                color: varianceColor(metrics.vac, metrics.bac),
              }}>
                {fmtVariance(metrics.vac, budgetCurrency)}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Progress bar */}
      <div className={styles.coverageRow}>
        <div className={styles.coverageHeader}>
          <span className={styles.coverageLabel}>
            {tKpi("budget.evm.progress")}
          </span>
          <span className={styles.coverageValue}>
            {metrics.weightedPctComplete}%
          </span>
        </div>
        <div className={styles.coverageTrack}>
          <div
            className={styles.coverageFill}
            style={{
              width: `${Math.min(100, metrics.weightedPctComplete)}%`,
              background: metrics.weightedPctComplete >= 100
                ? "var(--info-text)"
                : "var(--color-success)",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default EvmLitePanel;
